// public/js/base/baseManager.js

import { createLogger } from '@utils/logger.js';
import { formatDate, formatDateBySchema, formatDateForFormInput } from '@utils/dateUtils.js';

/**
 * BaseManager - Schema-driven object handling + data logic
 * Handles: CRUD, validation using schema, formatting, display logic, data transformations
 * Uses schema to auto-generate form configurations, validation rules, etc.
 * NO event handling, NO API calls (those are in Controller)
 */
class BaseManager {
    constructor(ObjectClass, options = {}) {
        const instanceKey = `${this.constructor.name}_instance`;
        if (this.constructor[instanceKey]) {
            return this.constructor[instanceKey];
        }
        this.constructor[instanceKey] = this;

        this.logger = createLogger('BaseManager');
        this.ObjectClass = ObjectClass;
        this.options = options;
        this.data_set = [];
        this.eventListeners = {};
        
        // Cache a sample object to get schema
        this._sampleObject = new this.ObjectClass();
        this._schema = this._sampleObject.getSchema();

        this.cacheState = {
            isFullDatasetLoaded: false,
            loadedEntityIds: new Set(),
            loadedSubjects: new Map(), // Track loaded subjects: 'dog:abc123' => true
            lastFullLoadTime: null,
            individualLoadTimes: new Map(),
            forceRefreshOnNext: false
        };
        
        this.cacheConfig = {
            maxAge: options.cacheMaxAge || 300000,
            autoCleanup: options.autoCleanup !== false,
            fullDatasetTTL: options.fullDatasetTTL || 600000,
            individualTTL: options.individualTTL || 300000
        };

        this.cardActionVisibility = {
            view: false,
            edit: true, 
            restore: false,
            delete: true,
            copy: true,
            print: true,
            download: false,
            share: false,
            unlink: false
        };
        
        this.logger.info(`Manager initialized for ${ObjectClass.name}`, {
            cacheConfig: this.cacheConfig,
            autoCleanup: this.cacheConfig.autoCleanup,
            schemaFields: Object.keys(this._schema).length
        });
        
        if (this.cacheConfig.autoCleanup) {
            this.startCacheCleanup();
        }
    }

    // ===============================
    // SCHEMA INTERPRETATION METHODS
    // ===============================

    /**
     * Get the schema definition
     * @returns {Object} Schema definition
     */
    getSchema() {
        return this._schema;
    }

    getDataSource(dataSource){
        return [];
    }

    /**
     * Get schema for a specific field
     * @param {string} fieldName - Field name
     * @returns {Object|null} Field schema or null if not found
     */
    getFieldSchema(fieldName) {
        return this._schema[fieldName] || null;
    }

    /**
     * Get all field names from schema
     * @returns {Array} Array of field names
     */
    getFieldNames() {
        return Object.keys(this._schema);
    }

    /**
     * Get fields that should be shown in forms
     * @returns {Array} Array of field names
     */
    getFormFields() {
        return this.getFieldNames().filter(fieldName => {
            const fieldSchema = this._schema[fieldName];
            return fieldSchema.showInForm !== false;
        });
    }

    /**
     * Get fields that should be shown in lists/cards
     * @returns {Array} Array of field names
     */
    getListFields() {
        return this.getFieldNames().filter(fieldName => {
            const fieldSchema = this._schema[fieldName];
            return fieldSchema.showInList === true;
        });
    }

    /**
     * Get required field names
     * @returns {Array} Array of required field names
     */
    getRequiredFields() {
        return this.getFieldNames().filter(fieldName => {
            const fieldSchema = this._schema[fieldName];
            return fieldSchema.required === true;
        });
    }

    /**
     * Get filterable field names
     * @returns {Array} Array of filterable field names
     */
    getFilterableFields() {
        return this.getFieldNames().filter(fieldName => {
            const fieldSchema = this._schema[fieldName];
            return fieldSchema.filterable === true;
        });
    }

    /**
     * Get field label from schema
     * @param {string} fieldName - Field name
     * @returns {string} Field label or formatted field name
     */
    getFieldLabel(fieldName) {
        const fieldSchema = this.getFieldSchema(fieldName);
        if (fieldSchema && fieldSchema.label) {
            return fieldSchema.label;
        }
        // Auto-generate label from field name
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    /**
     * Get field help text from schema
     * @param {string} fieldName - Field name
     * @returns {string|null} Help text or null
     */
    getFieldHelpText(fieldName) {
        const fieldSchema = this.getFieldSchema(fieldName);
        return fieldSchema ? fieldSchema.helpText || null : null;
    }

    /**
     * Get field options from schema (for select fields)
     * @param {string} fieldName - Field name
     * @returns {Array|null} Options array or null
     */
    getFieldOptions(fieldName) {
        const fieldSchema = this.getFieldSchema(fieldName);
        return fieldSchema ? fieldSchema.options || null : null;
    }

    /**
     * Check if field is required according to schema
     * @param {string} fieldName - Field name
     * @returns {boolean} True if required
     */
    isFieldRequired(fieldName) {
        const fieldSchema = this.getFieldSchema(fieldName);
        return fieldSchema ? fieldSchema.required === true : false;
    }

    /**
     * Get field type from schema
     * @param {string} fieldName - Field name
     * @returns {string|null} Field type or null if not found
     */
    getFieldType(fieldName) {
        const fieldSchema = this.getFieldSchema(fieldName);
        return fieldSchema ? fieldSchema.type : null;
    }

    /**
     * Get field constraints from schema (min, max, minLength, maxLength, etc.)
     * @param {string} fieldName - Field name
     * @returns {Object} Constraints object
     */
    getFieldConstraints(fieldName) {
        const fieldSchema = this.getFieldSchema(fieldName);
        if (!fieldSchema) return {};

        const constraints = {};
        const constraintKeys = ['min', 'max', 'minLength', 'maxLength', 'step', 'pattern'];
        
        constraintKeys.forEach(key => {
            if (fieldSchema.hasOwnProperty(key)) {
                constraints[key] = fieldSchema[key];
            }
        });

        return constraints;
    }

    /**
     * Get activities by subject (dog)
     * @param {string} subjectType - Subject type (e.g., 'dog')
     * @param {string} subjectId - Subject ID
     * @returns {Array} Array of matching activities
     */
    getEntitiesBySubject(subjectType, subjectId) {
        return this.data_set.filter(entity => {
            if (!entity.subjects || !Array.isArray(entity.subjects)) {
                return false;
            }
            
            return entity.subjects.some(subject => 
                subject.subject_type === subjectType && subject.subject_id === subjectId
            );
        });
    }

    // ===============================
    // DATE/TIME FORMATTING (Uses dateUtils)
    // ===============================

    /**
     * Format date according to user preferences
     * @param {string|Date} date - Date value to format
     * @param {boolean} includeTime - Whether to include time
     * @returns {string} Formatted date string
     */
    formatDate(date, includeTime = false) {
        return formatDate(date, { includeTime });
    }

    /**
     * Format field value for form inputs (HTML compatible)
     * @param {*} value - Field value
     * @param {Object} fieldConfig - Field configuration
     * @returns {string} Formatted value for form input
     */
    formatFieldValueForForm(value, fieldConfig) {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        switch (fieldConfig.type) {
            case 'datetime-local':
            case 'date':
            case 'datetime':
                // Use core date utility for form input formatting
                return formatDateForFormInput(value, fieldConfig);
            case 'number':
            case 'integer':
                return value.toString();
            case 'boolean':
                return value ? 'true' : 'false';
            default:
                return value.toString();
        }
    }

    // ===============================
    // SCHEMA-DRIVEN FORM DATA PROCESSING
    // ===============================

    /**
     * Process form data using schema definitions
     * Converts string values from HTML forms to proper types
     * @param {Object} formData - Raw form data (strings from HTML forms)
     * @returns {Object} Properly typed data ready for API/validation
     */
    processFormData(formData) {
        const processedData = { ...formData };
        
        this.getFieldNames().forEach(fieldName => {
            if (processedData.hasOwnProperty(fieldName)) {
                processedData[fieldName] = this.convertFieldValue(
                    processedData[fieldName], 
                    fieldName
                );
            }
        });
        
        return processedData;
    }

    /**
     * Convert field value based on schema type
     * @param {*} value - Raw value
     * @param {string} fieldName - Field name
     * @returns {*} Converted value
     */
    convertFieldValue(value, fieldName) {
        const fieldType = this.getFieldType(fieldName);
        const fieldSchema = this.getFieldSchema(fieldName);
        
        // Handle empty values
        if (value === null || value === undefined || value === '') {
            return fieldSchema.default || '';
        }

        switch (fieldType) {
            case 'number':
                const num = parseFloat(value);
                return isNaN(num) ? (fieldSchema.default || 0) : num;
            case 'integer':
                const int = parseInt(value);
                return isNaN(int) ? (fieldSchema.default || 0) : int;
            case 'boolean':
                return Boolean(value);
            case 'date':
                if (value instanceof Date) {
                    return value.toISOString().split('T')[0];
                }
                // Validate date format
                const date = new Date(value);
                if (isNaN(date.getTime())) {
                    throw new Error(`Invalid date format for ${fieldName}`);
                }
                return value;
            case 'datetime-local':
                if (value instanceof Date) {
                    return value.toISOString();
                }
                // Validate datetime format and convert to ISO string
                const datetime = new Date(value);
                if (isNaN(datetime.getTime())) {
                    throw new Error(`Invalid datetime format for ${fieldName}`);
                }
                return datetime.toISOString();
            case 'select':
                // Validate against options if available
                const options = this.getFieldOptions(fieldName);
                if (options && options.length > 0) {
                    const validValues = options.map(opt => opt.value || opt);
                    if (!validValues.includes(value)) {
                        return fieldSchema.default || validValues[0];
                    }
                }
                return value;
            default:
                return value.toString().trim();
        }
    }

    // ===============================
    // SCHEMA-DRIVEN VALIDATION
    // ===============================

    /**
     * Validate an object using schema-defined rules
     * @param {Object} obj - Object to validate
     * @param {boolean} isPlaceholder - If true, skip required field validation for empty placeholder entities
     * @returns {Object} Validation result
     */
    validate(obj, isPlaceholder = false) {
        const errors = [];
        
        // Check all schema fields
        this.getFieldNames().forEach(fieldName => {
            const fieldSchema = this.getFieldSchema(fieldName);
            const value = obj[fieldName];
            
            // Required field validation - skip for placeholder entities
            if (fieldSchema.required && !isPlaceholder && (value === undefined || value === null || value === '')) {
                errors.push(`${this.getFieldLabel(fieldName)} is required`);
                return;
            }
            
            // Skip other validation if field is empty and not required (or is placeholder)
            if (value === undefined || value === null || value === '' || (isPlaceholder && fieldSchema.required)) {
                return;
            }
            
            // Type validation
            if (!this.validateFieldType(value, fieldSchema.type)) {
                errors.push(`${this.getFieldLabel(fieldName)} must be of type ${fieldSchema.type}`);
            }
            
            // Constraint validation
            const constraintError = this.validateFieldConstraints(value, fieldName, fieldSchema);
            if (constraintError) {
                errors.push(constraintError);
            }
            
            // Custom validation function
            if (fieldSchema.validate && typeof fieldSchema.validate === 'function') {
                const customResult = fieldSchema.validate(value, obj);
                if (customResult !== true) {
                    errors.push(customResult || `${this.getFieldLabel(fieldName)} validation failed`);
                }
            }
        });
        
        if (errors.length > 0 && !isPlaceholder) {
            this.logger.debug(`Validation failed for object ${obj.id}:`, errors);
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Validate field type
     * @param {*} value - Value to validate
     * @param {string} expectedType - Expected type
     * @returns {boolean} True if valid
     */
    validateFieldType(value, expectedType) {
        switch (expectedType) {
            case 'string':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number' && !isNaN(value);
            case 'integer':
                return Number.isInteger(value);
            case 'boolean':
                return typeof value === 'boolean';
            case 'date':
            case 'datetime-local':
                return value instanceof Date || !isNaN(Date.parse(value));
            case 'email':
                return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            case 'url':
                try {
                    new URL(value);
                    return true;
                } catch {
                    return false;
                }
            default:
                return true;
        }
    }

    /**
     * Validate field constraints
     * @param {*} value - Value to validate
     * @param {string} fieldName - Field name
     * @param {Object} fieldSchema - Field schema
     * @returns {string|null} Error message or null if valid
     */
    validateFieldConstraints(value, fieldName, fieldSchema) {
        const label = this.getFieldLabel(fieldName);
        
        // String length validation
        if (typeof value === 'string') {
            if (fieldSchema.minLength && value.length < fieldSchema.minLength) {
                return `${label} must be at least ${fieldSchema.minLength} characters`;
            }
            if (fieldSchema.maxLength && value.length > fieldSchema.maxLength) {
                return `${label} must be no more than ${fieldSchema.maxLength} characters`;
            }
        }
        
        // Number range validation
        if (typeof value === 'number') {
            if (fieldSchema.min !== undefined && value < fieldSchema.min) {
                return `${label} must be at least ${fieldSchema.min}`;
            }
            if (fieldSchema.max !== undefined && value > fieldSchema.max) {
                return `${label} must be no more than ${fieldSchema.max}`;
            }
        }
        
        // Pattern validation
        if (fieldSchema.pattern && typeof value === 'string') {
            const regex = new RegExp(fieldSchema.pattern);
            if (!regex.test(value)) {
                return `${label} format is invalid`;
            }
        }
        
        return null;
    }

    // ===============================
    // SCHEMA-DRIVEN UI DATA PREPARATION
    // ===============================

    /**
     * Generate form field configurations from schema
     * @returns {Array} Array of form field configurations for views
     */
    generateFormConfig() {
        const formFields = this.getFormFields();
        
        return formFields.map(fieldName => {
            const fieldSchema = this.getFieldSchema(fieldName);
            const constraints = this.getFieldConstraints(fieldName);
            
            return {
                name: fieldName,
                type: fieldSchema.type || 'text',
                label: this.getFieldLabel(fieldName),
                required: this.isFieldRequired(fieldName),
                helpText: this.getFieldHelpText(fieldName),
                options: this.getFieldOptions(fieldName),
                searchable: fieldSchema.searchable || false,
                searchable_data: this.getDataSource(fieldName),
                placeholder: fieldSchema.placeholder,
                searchPlaceholder: fieldSchema.searchPlaceholder,
                noResultsText: fieldSchema.noResultsText,
                valueField: fieldSchema.valueField,
                textField: fieldSchema.textField,  
                ...constraints
            };
        });
    }

    /**
     * Generate filter configurations from schema - IMPROVED FOR MULTIPLE DATE RANGES & DATALISTS
     * Creates date range filters for EACH filterable date field (e.g., date_start_range, date_end_range)
     * Supports relationship fields with datalist for flexible text search
     * @returns {Array} Array of filter configurations for views
     */
    generateFilterConfig() {
        const filterableFields = this.getFilterableFields();
        const filterConfigs = [];

        // Process each filterable field
        filterableFields.forEach(fieldName => {
            const fieldSchema = this.getFieldSchema(fieldName);

            // Handle DATE fields: Create start/end range for EACH date field
            if (fieldSchema.type === 'date' || fieldSchema.type === 'datetime-local') {
                const baseLabel = this.getFieldLabel(fieldName);

                // Add Start filter for this date field
                filterConfigs.push({
                    name: `${fieldName}_start`,
                    type: 'date',
                    label: `${baseLabel} (From)`,
                    dateFieldName: fieldName  // Track which entity field this filters
                });

                // Add End filter for this date field
                filterConfigs.push({
                    name: `${fieldName}_end`,
                    type: 'date',
                    label: `${baseLabel} (To)`,
                    dateFieldName: fieldName  // Track which entity field this filters
                });
                return;
            }

            // Handle RELATIONSHIP fields: Use datalist for flexible search
            if (fieldSchema.type === 'relationship') {
                filterConfigs.push({
                    name: fieldName,
                    type: fieldSchema.searchable ? 'datalist' : 'select',
                    label: this.getFieldLabel(fieldName),
                    options: this.getFieldOptions(fieldName),
                    searchable: fieldSchema.searchable || false,
                    searchable_data: this.getDataSource(fieldName),
                    relationshipType: fieldSchema.relationshipType
                });
                return;
            }

            // Handle SELECT fields: Use dropdown with options
            if (fieldSchema.type === 'select') {
                filterConfigs.push({
                    name: fieldName,
                    type: fieldSchema.searchable ? 'datalist' : 'select',
                    label: this.getFieldLabel(fieldName),
                    options: this.getFieldOptions(fieldName),
                    searchable: fieldSchema.searchable || false,
                    searchable_data: this.getDataSource(fieldName)
                });
                return;
            }

            // Handle CHECKBOX fields: Use dropdown with Yes/No
            if (fieldSchema.type === 'checkbox') {
                filterConfigs.push({
                    name: fieldName,
                    type: 'select',
                    label: this.getFieldLabel(fieldName),
                    options: [
                        { value: 'true', text: 'Yes' },
                        { value: 'false', text: 'No' }
                    ]
                });
                return;
            }

            // Handle all other fields: Use text input
            filterConfigs.push({
                name: fieldName,
                type: 'text',
                label: this.getFieldLabel(fieldName)
            });
        });

        return filterConfigs;
    }

    /**
     * Prepare form data for views using schema
     * @param {Object} entity - Entity object
     * @param {boolean} isNew - Is this a new entity
     * @returns {Object} Form data ready for view
     */
    prepareFormData(entity, isNew = false) {
        const entityName = this.ObjectClass.name.toLowerCase().replace('object', '');
        
        return {
            title: isNew ? `Create New ${this.getDisplayName(entity)}` : `Edit ${this.getDisplayName(entity)}`,
            fields: this.generateFormConfig().map(fieldConfig => ({
                ...fieldConfig,
                value: this.formatFieldValueForForm(entity[fieldConfig.name], fieldConfig)
            })),
            buttons: isNew ? [
                { id: `${entityName}-edit-create`, text: 'Create', className: 'save-button' },
                { id: `${entityName}-edit-abort`, text: 'Cancel', className: 'cancel-button' }
            ] : [
                { id: `${entityName}-edit-save`, text: 'Save Changes', className: 'save-button' },
                { id: `${entityName}-edit-cancel`, text: 'Cancel', className: 'cancel-button' }
            ]
        };
    }

    // ===============================
    // DISPLAY VALUE PROCESSING (CENTRALIZED)
    // ===============================

    /**
     * Process field value for display (cards, details, etc.) with European formatting
     * @param {Object} entity - Entity object
     * @param {string} fieldName - Field name
     * @param {Object} fieldSchema - Field schema
     * @returns {string} Processed field value
     */
    processDisplayFieldValue(entity, fieldName, fieldSchema) {
        const rawValue = entity[fieldName];
        
        // Handle different field types with European formatting
        switch (fieldSchema.type) {
            case 'object':
                // Return the object as-is for custom processing by views
                // Don't convert to string for objects that need custom rendering
                if (rawValue && typeof rawValue === 'object') {
                    return rawValue;
                }
                return fieldSchema.default || {};
                
            case 'date':
            case 'datetime-local':
            case 'datetime':
                // Use core date utility for all date types
                return formatDateBySchema(rawValue, fieldSchema);
                
            case 'number':
                if (rawValue > 0) {
                    // Add metric units based on field name
                    if (fieldName.includes('weight')) {
                        return `${rawValue} kg`;
                    } else if (fieldName.includes('height') || fieldName.includes('length')) {
                        return `${rawValue} cm`;
                    } else if (fieldName.includes('distance')) {
                        return `${rawValue} m`;
                    } else if (fieldName.includes('temperature')) {
                        return `${rawValue} °C`;
                    } else {
                        return rawValue.toString();
                    }
                }
                return 'Not specified';
                
            case 'integer':
                return rawValue !== null && rawValue !== undefined ? rawValue.toString() : 'Not specified';
                
            case 'select':
                // Capitalize first letter for display
                return rawValue ? rawValue.charAt(0).toUpperCase() + rawValue.slice(1) : 'Not specified';
                
            case 'boolean':
                return rawValue ? 'Yes' : 'No';
                
            default:
                return rawValue || 'Not specified';
        }
    }

    // ===============================
    // BASE CRUD OPERATIONS (Fixed validation)
    // ===============================

    /**
     * Mark the full dataset as loaded and update cache timestamps
     */
    markFullDatasetLoaded() {
        this.cacheState.isFullDatasetLoaded = true;
        this.cacheState.lastFullLoadTime = Date.now();
        
        this.data_set.forEach(entity => {
            this.cacheState.loadedEntityIds.add(entity.id);
            this.cacheState.individualLoadTimes.set(entity.id, Date.now());
        });
        
        this.logger.cache(`Full dataset loaded: ${this.data_set.length} entities`);
        this.emit('fullDatasetLoaded', this.data_set);
    }

    markEntityLoaded(entityId) {
        this.cacheState.loadedEntityIds.add(entityId);
        this.cacheState.individualLoadTimes.set(entityId, Date.now());
        this.logger.cache(`Individual entity loaded: ${entityId}`);
        this.emit('entityLoaded', entityId);
    }

    needsFullDatasetRefresh() {
        if (this.cacheState.forceRefreshOnNext) {
            this.logger.cache('Force refresh requested');
            return true;
        }
        
        if (!this.cacheState.isFullDatasetLoaded) {
            this.logger.cache('Full dataset never loaded');
            return true;
        }
        
        const now = Date.now();
        const age = now - this.cacheState.lastFullLoadTime;
        if (age > this.cacheConfig.fullDatasetTTL) {
            this.logger.cache(`Full dataset expired (${Math.round(age/1000)}s old)`);
            return true;
        }
        
        this.logger.cache(`Full dataset is fresh (${Math.round(age/1000)}s old)`);
        return false;
    }

    /**
     * Create entity with optional validation skip for placeholder entities
     * @param {Object} data - Entity data
     * @param {boolean} skipValidation - Skip validation for placeholder entities
     * @returns {Object} Created entity
     */
    create(data = {}, skipValidation = false) {
        this.logger.startTimer('create');
        
        if (data.id && this.exists(data.id)) {
            this.logger.debug(`Entity ${data.id} exists, updating instead`);
            return this.update(data);
        }
        
        try {
            const data_element = new this.ObjectClass(data);
            data_element._isNew = true; 
            
            // Skip validation for placeholder entities (used in "create new" workflows)
            if (!skipValidation) {
                const validation = this.validate(data_element, skipValidation);
                if (!validation.isValid) {
                    this.logger.error(`Validation failed for new entity:`, validation.errors);
                    throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
                }
            } else {
                this.logger.debug('Skipping validation for placeholder entity');
            }
            
            this.data_set.push(data_element);
            
            if (data_element.id) {
                this.markEntityLoaded(data_element.id);
            }
            
            this.logger.success(`Created entity: ${data_element.id} (validation skipped: ${skipValidation})`);
            this.emit('create', data_element);
            return data_element;
        } catch (error) {
            this.logger.error('Create operation failed:', error);
            throw error;
        } finally {
            this.logger.endTimer('create');
        }
    }

    /**
     * Copy an existing entity with new ID and timestamps
     * @param {string} id - Entity ID to copy
     * @param {Object} overrides - Optional field overrides for the copy
     * @returns {Object|null} Copied entity or null if source not found
     */
    copy(id, overrides = {}) {
        const sourceEntity = this.read(id);
        if (!sourceEntity) {
            this.logger.warn(`Copy failed: entity ${id} not found`);
            return null;
        }

        // Get source data and create copy with new timestamps
        const sourceData = sourceEntity.toJSON ? sourceEntity.toJSON() : { ...sourceEntity };
        const copyData = {
            ...sourceData,
            ...overrides,
            id: crypto.randomUUID(), // Let ObjectClass generate new ID
            title: overrides.title || (sourceData.title ? `Copy of ${sourceData.title}` : sourceData.title),
            date_creation: new Date().toISOString(),
            date_update: new Date().toISOString()
        };

        // Create and validate new entity
        const copiedEntity = new this.ObjectClass(copyData);
        const validation = this.validate(copiedEntity);
        
        if (!validation.isValid) {
            throw new Error(`Copy validation failed: ${validation.errors.join(', ')}`);
        }

        // Add to dataset
        this.data_set.push(copiedEntity);
        this.markEntityLoaded(copiedEntity.id);
        
        this.logger.success(`Copied entity: ${id} → ${copiedEntity.id}`);
        this.emit('copy', copiedEntity, sourceEntity);
        
        return copiedEntity;
    }



    read(id = null) {
        if (id === null) {
            this.logger.debug(`Reading all entities: ${this.data_set.length} found`);
            return this.data_set;
        }
        
        const entity = this.data_set.find(data_element => data_element.id === id);
        this.logger.debug(`Reading entity ${id}: ${entity ? 'found' : 'not found'}`);
        return entity;
    }

    update(update_data) {
        this.logger.startTimer('update');

        try {
            const data_index = this.data_set.findIndex(data_element => data_element.id === update_data.id);
            if (data_index !== -1) {
                const old_object = this.data_set[data_index];
                const new_object = new this.ObjectClass({
                    ...old_object.toJSON(),
                    ...update_data,
                    date_update: new Date().toISOString()
                });

                // CRITICAL: Preserve the _isNew flag from old object if it exists
                if (old_object._isNew !== undefined) {
                    new_object._isNew = old_object._isNew;
                }

                const validation = this.validate(new_object);
                if (!validation.isValid) {
                    this.logger.error(`Validation failed for update:`, validation.errors);
                    throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
                }

                this.data_set[data_index] = new_object;
                this.logger.success(`Updated entity: ${update_data.id}${new_object._isNew ? ' (still marked as new)' : ''}`);
                this.emit('update', new_object, old_object);
                return new_object;
            }

            this.logger.warn(`Update failed: entity ${update_data.id} not found`);
            return null;
        } catch (error) {
            this.logger.error('Update operation failed:', error);
            throw error;
        } finally {
            this.logger.endTimer('update');
        }
    }

    delete(id) {
        this.logger.startTimer('delete');
        
        try {
            const data_index = this.data_set.findIndex(data_element => data_element.id === id); 
            if (data_index !== -1) {
                const [removed_data] = this.data_set.splice(data_index, 1);
                
                this.cacheState.loadedEntityIds.delete(id);
                this.cacheState.individualLoadTimes.delete(id);
                
                this.logger.success(`Deleted entity: ${id}`);
                this.emit('delete', removed_data);
                return removed_data;
            }
            
            this.logger.warn(`Delete failed: entity ${id} not found`);
            return null;
        } catch (error) {
            this.logger.error('Delete operation failed:', error);
            throw error;
        } finally {
            this.logger.endTimer('delete');
        }
    }

    clear() {
        const oldCount = this.data_set.length;
        const oldData = [...this.data_set];
        this.data_set = [];
        this.resetCacheState();
        this.logger.info(`Cleared all entities: ${oldCount} removed`);
        this.emit('clear', oldData);
    }

    createBulk(dataArray) {
        if (!Array.isArray(dataArray)) {
            this.logger.error('createBulk expects an array of data objects');
            throw new Error('createBulk expects an array of data objects');
        }

        if (dataArray.length === 0) {
            this.logger.debug('createBulk called with empty array');
            return [];
        }

        this.logger.startTimer('createBulk');
        
        const processedObjects = [];
        const createdCount = { new: 0, updated: 0 };
        
        try {
            for (const data of dataArray) {
                if (data === null || data === undefined) {
                    this.logger.warn('Skipping null/undefined data item in createBulk');
                    continue;
                }
                
                if (data.id && this.exists(data.id)) {
                    const updated = this.update(data);
                    if (updated) {
                        processedObjects.push(updated);
                        createdCount.updated++;
                    }
                } else {
                    const data_element = new this.ObjectClass(data);
                    
                    const validation = this.validate(data_element);
                    if (!validation.isValid) {
                        this.logger.warn(`Skipping invalid object: ${validation.errors.join(', ')}`);
                        continue;
                    }
                    
                    this.data_set.push(data_element);
                    processedObjects.push(data_element);
                    createdCount.new++;
                    
                    if (data_element.id) {
                        this.markEntityLoaded(data_element.id);
                    }
                }
            }
            
            this.logger.success(`Bulk create completed: ${createdCount.new} new, ${createdCount.updated} updated`);
            this.emit('bulkCreate', processedObjects, createdCount);
            return processedObjects;
            
        } catch (error) {
            const rollbackCount = createdCount.new;
            this.data_set.splice(-rollbackCount, rollbackCount);
            this.logger.error(`createBulk failed, rolled back ${rollbackCount} items:`, error);
            throw new Error(`createBulk failed: ${error.message}`);
        } finally {
            this.logger.endTimer('createBulk');
        }
    }

    // Continue with all other existing methods...
    getDisplayName(obj) {
        const fallbackName = this.ObjectClass.name.endsWith('Object') 
            ? this.ObjectClass.name.slice(0, -6) 
            : this.ObjectClass.name;
        
        // Check each field exists AND has a meaningful value
        const name = obj?.name?.trim();
        const title = obj?.title?.trim(); 
        const nickName = obj?.nick_name?.trim();
        
        return name || title || nickName || fallbackName;
    }


    count() {
        return this.data_set.length;
    }

    exists(id) {
        return this.data_set.some(data_element => data_element.id === id);
    }

    findBy(property, value) {
        const results = this.data_set.filter(data_element => data_element[property] === value);
        this.logger.debug(`findBy ${property}=${value}: ${results.length} results`);
        return results;
    }

    search(searchTerm, fields = ['name', 'title', 'nick_name', 'description']) {
        if (!searchTerm || searchTerm.trim() === '') {
            this.logger.debug('Empty search term, returning all entities');
            return this.data_set;
        }
        
        const term = searchTerm.toLowerCase();
        const results = this.data_set.filter(data_element => {
            return fields.some(field => {
                const value = data_element[field];
                return value && typeof value === 'string' && value.toLowerCase().includes(term);
            });
        });
        
        this.logger.debug(`Search "${searchTerm}": ${results.length} results in fields [${fields.join(', ')}]`);
        return results;
    }

    /**
     * Mark that data for a specific subject has been loaded
     * @param {string} subjectType - Type of subject (e.g., 'dog', 'activity')
     * @param {string} subjectId - ID of the subject
     */
    markSubjectLoaded(subjectType, subjectId) {
        const key = `${subjectType}:${subjectId}`;
        this.cacheState.loadedSubjects.set(key, true);
        this.logger.cache(`Marked subject as loaded: ${key}`);
        this.emit('subjectLoaded', { subjectType, subjectId });
    }

    /**
     * Check if data for a specific subject has been loaded
     * @param {string} subjectType - Type of subject (e.g., 'dog', 'activity')
     * @param {string} subjectId - ID of the subject
     * @returns {boolean} True if subject data is already cached
     */
    isSubjectLoaded(subjectType, subjectId) {
        // If full dataset is loaded, all subjects are available
        if (this.cacheState.isFullDatasetLoaded) {
            return true;
        }

        const key = `${subjectType}:${subjectId}`;
        return this.cacheState.loadedSubjects.has(key);
    }

    /**
     * Determine if we need to fetch data for a specific subject
     * @param {string} subjectType - Type of subject (e.g., 'dog', 'activity')
     * @param {string} subjectId - ID of the subject
     * @returns {boolean} True if data should be fetched from API
     */
    shouldFetchBySubject(subjectType, subjectId) {
        // If we have everything, no need to fetch
        if (this.cacheState.isFullDatasetLoaded) {
            this.logger.cache(`No fetch needed - full dataset loaded`);
            return false;
        }

        // If we already loaded this subject, no need to fetch
        const key = `${subjectType}:${subjectId}`;
        const alreadyLoaded = this.cacheState.loadedSubjects.has(key);

        if (alreadyLoaded) {
            this.logger.cache(`No fetch needed - subject ${key} already loaded`);
        } else {
            this.logger.cache(`Fetch needed for subject: ${key}`);
        }

        return !alreadyLoaded;
    }

    /**
     * Clear loaded subject tracking for a specific subject
     * @param {string} subjectType - Type of subject (e.g., 'dog', 'activity')
     * @param {string} subjectId - ID of the subject
     */
    clearSubjectCache(subjectType, subjectId) {
        const key = `${subjectType}:${subjectId}`;
        this.cacheState.loadedSubjects.delete(key);
        this.logger.cache(`Cleared subject cache: ${key}`);
        this.emit('subjectCacheCleared', { subjectType, subjectId });
    }

    resetCacheState() {
        this.logger.cache('Resetting cache state');
        this.cacheState = {
            isFullDatasetLoaded: false,
            loadedEntityIds: new Set(),
            lastFullLoadTime: null,
            individualLoadTimes: new Map(),
            forceRefreshOnNext: false,
            currentContext: null,
            contextCaches: new Map()
        };
        this.emit('cacheReset');
    }

    getCacheStats() {
        const now = Date.now();
        return {
            totalEntities: this.data_set.length,
            isFullDatasetLoaded: this.cacheState.isFullDatasetLoaded,
            fullDatasetAge: this.cacheState.lastFullLoadTime ? now - this.cacheState.lastFullLoadTime : null,
            loadedEntityCount: this.cacheState.loadedEntityIds.size,
            loadedEntityIds: Array.from(this.cacheState.loadedEntityIds),
            forceRefreshPending: this.cacheState.forceRefreshOnNext
        };
    }

    // Event system
    on(event, callback) {
        if (!this.eventListeners[event]) {
            this.eventListeners[event] = [];
        }
        this.eventListeners[event].push(callback);
        this.logger.debug(`Event listener added for: ${event}`);
    }

    emit(event, ...args) {
        if (this.eventListeners[event]) {
            this.logger.debug(`Emitting event: ${event} to ${this.eventListeners[event].length} listeners`);
            this.eventListeners[event].forEach(callback => {
                try {
                    callback(...args);
                } catch (error) {
                    this.logger.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }

    startCacheCleanup() {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
        }
        
        this.cleanupInterval = setInterval(() => {
            this.cleanupExpiredObjects();
        }, 300000);
        
        this.logger.debug('Cache cleanup interval started (5 minutes)');
    }

    cleanupExpiredObjects() {
        if (!this.cacheConfig.maxAge) return;
        
        const now = Date.now();
        const beforeCount = this.data_set.length;
        const expiredIds = [];
        
        this.data_set = this.data_set.filter(obj => {
            const age = now - new Date(obj.date_update).getTime();
            const isExpired = age > this.cacheConfig.maxAge;
            
            if (isExpired) {
                expiredIds.push(obj.id);
            }
            
            return !isExpired;
        });
        
        expiredIds.forEach(id => {
            this.cacheState.loadedEntityIds.delete(id);
            this.cacheState.individualLoadTimes.delete(id);
        });
        
        if (expiredIds.length > 0) {
            this.cacheState.isFullDatasetLoaded = false;
            this.logger.cache(`Cleaned up ${expiredIds.length} expired objects`);
        }
        
        const removedCount = beforeCount - this.data_set.length;
        if (removedCount > 0) {
            this.emit('cleanup', removedCount, expiredIds);
        }
    }


    // ADD THESE METHODS TO YOUR EXISTING BaseManager.js FILE:

    // ===============================
    // SCHEMA-DRIVEN DETAILS GENERATION
    // ===============================

    /**
     * Get fields grouped by section from schema - RESPECTS showInDetails flag
     * @returns {Object} Object with section names as keys and field arrays as values
     */
    getFieldsBySection() {
        const fieldsBySection = {};
        
        this.getFieldNames().forEach(fieldName => {
            const fieldSchema = this.getFieldSchema(fieldName);
            
            // Skip fields that shouldn't be shown in details
            if (fieldSchema.showInDetails === false) {
                return;
            }
            
            const section = fieldSchema.section || 'general';
            
            if (!fieldsBySection[section]) {
                fieldsBySection[section] = [];
            }
            
            fieldsBySection[section].push(fieldName);
        });
        
        return fieldsBySection;
    }

    /**
     * Get section definitions from ObjectClass (if available)
     * @returns {Object} Section definitions with titles and order
     */
    getSectionDefinitions() {
        if (this.ObjectClass.getSectionDefinitions) {
            return this.ObjectClass.getSectionDefinitions();
        }
        
        // Default fallback section definitions
        return {
            general: { title: 'General Information', order: 1 },
            system_info: { title: 'System Information', order: 99 }
        };
    }

    /**
     * Prepare details data for view rendering - ENHANCED to support all section properties
     * @param {Object} entity - Entity object
     * @param {Object} options - Display options
     * @returns {Object} Details data ready for view
     */
    prepareDetailsData(entity, options = {}) {
        const fieldsBySection = this.getFieldsBySection();
        const sectionDefinitions = this.getSectionDefinitions();
        
        // Build sections array
        const sections = [];
        
        // Sort sections by order
        const sortedSectionKeys = Object.keys(fieldsBySection).sort((a, b) => {
            const orderA = sectionDefinitions[a]?.order || 999;
            const orderB = sectionDefinitions[b]?.order || 999;
            return orderA - orderB;
        });
        
        sortedSectionKeys.forEach(sectionKey => {
            const sectionDef = sectionDefinitions[sectionKey] || {};
            const fieldNames = fieldsBySection[sectionKey];
            
            // Skip sections with no fields AND no custom render capability
            if ((!fieldNames || fieldNames.length === 0) && !sectionDef.customRender) {
                return;
            }
            
            const fields = [];
            
            // Process fields if they exist
            if (fieldNames && fieldNames.length > 0) {
                fieldNames.forEach(fieldName => {
                    const fieldSchema = this.getFieldSchema(fieldName);
                    if (!fieldSchema) return;
                    
                    // Process field value using centralized display logic
                    const value = this.processDisplayFieldValue(entity, fieldName, fieldSchema);
                    
                    fields.push({
                        name: fieldName,
                        label: this.getFieldLabel(fieldName),
                        value: value
                    });
                });
            }
            
            // Add calculated fields for this section
            const calculatedFields = this.getCalculatedFieldsForSection(entity, sectionKey);
            fields.push(...calculatedFields);
            
            // Create section if it has fields OR is marked for custom rendering
            if (fields.length > 0 || sectionDef.customRender) {
                sections.push({
                    // ✅ Copy ALL properties from section definition
                    ...sectionDef,
                    // Override with processed values
                    title: sectionDef.title || this.formatSectionName(sectionKey),
                    className: sectionKey.replace(/_/g, '-'),
                    fields: fields,
                    // Add entity reference for custom renderers
                    entityId: entity.id
                });
            }
        });
        
        return {
            id: entity.id,
            header: this.prepareDetailsHeader(entity, options),
            body: {
                title: this.prepareDetailsTitle(entity, options),
                sections: sections
            },
            actions: this.getDetailsActions ? this.getDetailsActions(entity, options) : []
        };
    }


    /**
     * Get details actions for entity (DEFAULT - can be overridden by subclasses)
     * @param {Object} entity - Entity object
     * @param {Object} options - Action options
     * @returns {Array} Array of action configurations
     */
    getDetailsActions(entity, options = {}) {
        // Default: use the same actions as cards
        return this.getCardActions(entity, options);
    }


    /**
     * Prepare card data for view rendering (DEFAULT - can be overridden by subclasses)
     * @param {Object} entity - Entity object
     * @param {Object} options - Display options
     * @returns {Object} Card data ready for view
     */
    prepareCardData(entity, options = {}) {
        // Get fields that should be shown in lists/cards from schema
        const listFields = this.getListFields();
        
        // Build fields array from schema using base processing
        const fields = [];
        
        listFields.forEach(fieldName => {
            const fieldSchema = this.getFieldSchema(fieldName);
            const value = this.processDisplayFieldValue(entity, fieldName, fieldSchema);
            
            fields.push({
                name: fieldName,
                label: this.getFieldLabel(fieldName),
                value: value || 'Not specified'
            });
        });

        return {
            id: entity.id,
            entityId: entity.id,
            type: this.entityType || 'entity',
            
            header: this.prepareCardHeader(entity, options),
            body: {
                title: this.getDisplayName(entity),
                fields: fields
            },
            actions: this.getCardActions(entity, options)
        };
    }

    
/**
     * Prepare card header for entity (DEFAULT - can be overridden by subclasses)
     * @param {Object} entity - Entity object
     * @param {Object} options - Options
     * @returns {Object} Header data
     */
    prepareCardHeader(entity, options = {}) {
        return {
            icon: 'description', // Default Material icon
            altText: `${this.entityType || 'Entity'}: ${this.getDisplayName(entity)}`
        };
    }

    /**
     * Get calculated fields for a specific section - OVERRIDE IN SUBCLASSES
     * @param {Object} entity - Entity object
     * @param {string} sectionKey - Section key
     * @returns {Array} Array of calculated field objects
     */
    getCalculatedFieldsForSection(entity, sectionKey) {
        // Base implementation returns empty - subclasses can override
        return [];
    }

    /**
     * Prepare details header data - OVERRIDE IN SUBCLASSES
     * @param {Object} entity - Entity object
     * @param {Object} options - Options
     * @returns {Object} Header data
     */
    prepareDetailsHeader(entity, options = {}) {
        return {
            imageUrl: entity.photo_url || null,
            altText: `Photo of ${this.getDisplayName(entity)}`
        };
    }

    /**
     * Prepare details title data - OVERRIDE IN SUBCLASSES
     * @param {Object} entity - Entity object  
     * @param {Object} options - Options
     * @returns {Object} Title data
     */
    prepareDetailsTitle(entity, options = {}) {
        return {
            main: this.getDisplayName(entity),
            subtitle: null
        };
    }

    /**
     * Format section name for display
     * @param {string} sectionKey - Section key
     * @returns {string} Formatted section name
     */
    formatSectionName(sectionKey) {
        return sectionKey
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    
    /**
     * Get card actions for entity (reads from cardActionVisibility configuration)
     * @param {Object} entity - Entity object
     * @param {Object} options - Action options
     * @returns {Array} Array of action configurations
     */
    getCardActions(entity, options = {}) {
        const actions = [];
        
        // Iterate through the cardActionVisibility configuration
        Object.keys(this.cardActionVisibility).forEach(actionName => {
            if (this.cardActionVisibility[actionName] === true) {
                // Look up action definition from static registry
                const actionDef = BaseManager.ACTION_DEFINITIONS[actionName];
                if (actionDef) {
                    actions.push({
                        name: actionDef.name,
                        icon: actionDef.icon,
                        title: actionDef.title,
                        cssClass: actionDef.cssClass,
                        entityId: entity.id
                    });
                }
            }
        });
        
        return actions;
    }




    buildActionsFromNames(allowedActionNames, entityId) {
        const actions = [];
        allowedActionNames.forEach(actionName => {
            if (this.cardActionVisibility[actionName]) {
                const actionDef = BaseManager.ACTION_DEFINITIONS[actionName];
                if (actionDef) {
                    actions.push({
                        name: actionDef.name,
                        icon: actionDef.icon,
                        title: actionDef.title,
                        cssClass: actionDef.cssClass,
                        entityId: entityId
                    });
                }
            }
        });
        return actions;
    }



    static ACTION_DEFINITIONS = {
        view: {
            name: 'view',
            icon: 'visibility',
            title: 'View',
            cssClass: 'view'
        },
        edit: {
            name: 'edit',
            icon: 'edit',
            title: 'Edit',
            cssClass: 'edit'
        },
        delete: {
            name: 'delete',
            icon: 'delete',
            title: 'Trash',
            cssClass: 'delete'
        },
        restore: {
            name: 'restore',
            icon: 'restore_from_trash',
            title: 'Restore',
            cssClass: 'restore'
        },
        copy: {
            name: 'copy',
            icon: 'content_copy',
            title: 'Copy',
            cssClass: 'copy'
        },
        print: {
            name: 'print',
            icon: 'print',
            title: 'Print',
            cssClass: 'print'
        },
        download: {
            name: 'download',
            icon: 'download',
            title: 'Download',
            cssClass: 'download'
        },
        share: {
            name: 'share',
            icon: 'share',
            title: 'Share',
            cssClass: 'share'
        },
        unlink: {
            name: 'unlink',
            icon: 'link_off',
            title: 'Unlink',
            cssClass: 'unlink'
        }
    };

}





export default BaseManager;